import React, { useState, useEffect } from 'react';
import { 
  UsersIcon,
  ShoppingBagIcon,
  ChatAlt2Icon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  TrendingUpIcon,
  ExclamationIcon,
  CheckCircleIcon,
  ClockIcon,
  EyeIcon,
  StarIcon,
  LocationMarkerIcon,
  PhoneIcon,
  MailIcon,
  CogIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  FilterIcon,
  SearchIcon,
  RefreshIcon,
  PlusIcon,
  MinusIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  BellIcon,
  GlobeAltIcon,
  CalendarIcon,
  UserGroupIcon,
  ClipboardListIcon,
  LightningBoltIcon,
  SparklesIcon,
  BanIcon,
  CheckIcon,
  XIcon,
  PencilIcon,
  TrashIcon,
  ArchiveIcon,
  FlagIcon,
  EyeOffIcon,
  LockClosedIcon,
  KeyIcon
} from '@heroicons/react/outline';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const AdminDashboard = () => {
  const { user, userProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Mock admin data - replace with real Firebase data
  const [adminData, setAdminData] = useState({
    stats: {
      totalUsers: 1247,
      totalSuppliers: 892,
      totalBuyers: 355,
      totalRFQs: 567,
      totalQuotes: 2341,
      totalRevenue: 45000000,
      activeChats: 89,
      pendingApprovals: 23,
      reportedIssues: 12,
      thisMonthUsers: 156,
      thisMonthRevenue: 8500000,
      avgResponseTime: 3.2,
      platformRating: 4.6
    },
    recentActivity: [
      {
        id: 1,
        type: 'user_registered',
        title: 'New supplier registered',
        user: 'TechSolutions NG',
        timestamp: new Date(Date.now() - 30 * 60 * 1000),
        status: 'pending_approval'
      },
      {
        id: 2,
        type: 'rfq_created',
        title: 'New RFQ created',
        user: 'Corporate Solutions Ltd',
        amount: 500000,
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
        status: 'active'
      },
      {
        id: 3,
        type: 'issue_reported',
        title: 'Payment issue reported',
        user: 'CleanPro Services',
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
        status: 'investigating'
      },
      {
        id: 4,
        type: 'chat_dispute',
        title: 'Chat dispute between users',
        users: ['Supplier A', 'Buyer B'],
        timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000),
        status: 'resolved'
      }
    ],
    topSuppliers: [
      {
        id: 1,
        name: 'TechSolutions NG',
        email: 'contact@techsolutions.ng',
        phone: '+234 801 234 5678',
        location: 'Lagos',
        rating: 4.8,
        totalQuotes: 45,
        totalRevenue: 2500000,
        status: 'active',
        joinedDate: '2024-01-15'
      },
      {
        id: 2,
        name: 'CleanPro Services',
        email: 'info@cleanpro.ng',
        phone: '+234 802 345 6789',
        location: 'Abuja',
        rating: 4.6,
        totalQuotes: 32,
        totalRevenue: 1800000,
        status: 'active',
        joinedDate: '2024-02-03'
      },
      {
        id: 3,
        name: 'OfficeMax Supplies',
        email: 'sales@officemax.ng',
        phone: '+234 803 456 7890',
        location: 'Lagos',
        rating: 4.7,
        totalQuotes: 38,
        totalRevenue: 2100000,
        status: 'suspended',
        joinedDate: '2024-01-28'
      }
    ],
    topBuyers: [
      {
        id: 1,
        name: 'Corporate Solutions Ltd',
        email: 'procurement@corporate.ng',
        phone: '+234 804 567 8901',
        location: 'Lagos',
        totalRFQs: 12,
        totalSpent: 3500000,
        status: 'active',
        joinedDate: '2024-01-10'
      },
      {
        id: 2,
        name: 'StartupHub Lagos',
        email: 'admin@startuphub.ng',
        phone: '+234 805 678 9012',
        location: 'Lagos',
        totalRFQs: 8,
        totalSpent: 1200000,
        status: 'active',
        joinedDate: '2024-02-15'
      },
      {
        id: 3,
        name: 'Restaurant Chain NG',
        email: 'supplies@restaurant.ng',
        phone: '+234 806 789 0123',
        location: 'Abuja',
        totalRFQs: 15,
        totalSpent: 2800000,
        status: 'inactive',
        joinedDate: '2024-01-22'
      }
    ],
    pendingApprovals: [
      {
        id: 1,
        type: 'supplier',
        name: 'NewTech Solutions',
        email: 'info@newtech.ng',
        category: 'IT Equipment',
        submittedDate: '2024-03-15',
        documents: ['Business License', 'Tax Certificate']
      },
      {
        id: 2,
        type: 'buyer',
        name: 'Global Enterprises',
        email: 'procurement@global.ng',
        category: 'Corporate',
        submittedDate: '2024-03-14',
        documents: ['Company Registration']
      }
    ],
    reportedIssues: [
      {
        id: 1,
        type: 'payment',
        user: 'CleanPro Services',
        description: 'Payment not received for completed work',
        severity: 'high',
        reportedDate: '2024-03-15',
        status: 'investigating'
      },
      {
        id: 2,
        type: 'dispute',
        users: ['Supplier A', 'Buyer B'],
        description: 'Dispute over service quality',
        severity: 'medium',
        reportedDate: '2024-03-14',
        status: 'resolved'
      }
    ],
    platformMetrics: {
      userGrowth: 15,
      revenueGrowth: 23,
      responseRate: 85,
      satisfactionScore: 4.6,
      avgSessionDuration: 12.5,
      bounceRate: 28
    }
  });

  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending_approval': return 'bg-yellow-100 text-yellow-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'investigating': return 'bg-orange-100 text-orange-800';
      case 'resolved': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'user_registered': return <UsersIcon className="h-5 w-5 text-blue-600" />;
      case 'rfq_created': return <DocumentTextIcon className="h-5 w-5 text-green-600" />;
      case 'issue_reported': return <ExclamationIcon className="h-5 w-5 text-red-600" />;
      case 'chat_dispute': return <ChatAlt2Icon className="h-5 w-5 text-orange-600" />;
      default: return <DocumentTextIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white shadow-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center mb-2">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center mr-4">
                    <ShieldCheckIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">
                      Twerce Admin Dashboard
                    </h1>
                    <p className="text-gray-600 mb-2">
                      Welcome back, {userProfile?.fullName || user?.displayName || 'Admin'}! 
                      Platform management overview.
                    </p>
                    <div className="flex items-center space-x-4 text-sm">
                      <span className="flex items-center text-green-600">
                        <UsersIcon className="h-4 w-4 mr-1" />
                        {adminData.stats.totalUsers} total users
                      </span>
                      <span className="flex items-center text-blue-600">
                        <DocumentTextIcon className="h-4 w-4 mr-1" />
                        {adminData.stats.totalRFQs} active RFQs
                      </span>
                      <span className="flex items-center text-orange-600">
                        <ExclamationIcon className="h-4 w-4 mr-1" />
                        {adminData.stats.pendingApprovals} pending approvals
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <button className="relative px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors">
                  <BellIcon className="h-5 w-5 mr-2 inline" />
                  Alerts
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {adminData.stats.reportedIssues}
                  </span>
                </button>
                <button 
                  onClick={() => navigate('/admin/settings')}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all duration-300 font-semibold"
                >
                  Settings
                </button>
                <button 
                  onClick={signOut}
                  className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-300 font-semibold flex items-center"
                >
                  <LockClosedIcon className="h-4 w-4 mr-2" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Users</p>
                <p className="text-3xl font-bold text-gray-900">{adminData.stats.totalUsers}</p>
                <div className="flex items-center mt-2">
                  <ArrowUpIcon className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600">+{adminData.stats.thisMonthUsers} this month</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <UsersIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-3xl font-bold text-gray-900">{formatCurrency(adminData.stats.totalRevenue)}</p>
                <div className="flex items-center mt-2">
                  <ArrowUpIcon className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600">+{formatCurrency(adminData.stats.thisMonthRevenue)} this month</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active RFQs</p>
                <p className="text-3xl font-bold text-gray-900">{adminData.stats.totalRFQs}</p>
                <div className="flex items-center mt-2">
                  <ArrowUpIcon className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600">+{adminData.stats.totalQuotes} quotes</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <DocumentTextIcon className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Platform Rating</p>
                <p className="text-3xl font-bold text-gray-900">{adminData.stats.platformRating}</p>
                <div className="flex items-center mt-2">
                  <StarIcon className="h-4 w-4 text-yellow-400 fill-current mr-1" />
                  <span className="text-sm text-gray-600">out of 5</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <StarIcon className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Metrics Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl border border-yellow-200 p-4 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-800">Pending Approvals</p>
                <p className="text-2xl font-bold text-yellow-900">{adminData.stats.pendingApprovals}</p>
                <p className="text-xs text-yellow-700">Need review</p>
              </div>
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <ClockIcon className="h-5 w-5 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-2xl border border-red-200 p-4 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-800">Reported Issues</p>
                <p className="text-2xl font-bold text-red-900">{adminData.stats.reportedIssues}</p>
                <p className="text-xs text-red-700">Need attention</p>
              </div>
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <ExclamationIcon className="h-5 w-5 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-4 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-800">Active Chats</p>
                <p className="text-2xl font-bold text-blue-900">{adminData.stats.activeChats}</p>
                <p className="text-xs text-blue-700">Ongoing</p>
              </div>
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <ChatAlt2Icon className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border border-green-200 p-4 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-800">Response Rate</p>
                <p className="text-2xl font-bold text-green-900">{adminData.stats.responseRate}%</p>
                <p className="text-xs text-green-700">Platform average</p>
              </div>
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUpIcon className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'overview', name: 'Overview', icon: EyeIcon },
                { id: 'users', name: 'User Management', icon: UsersIcon },
                { id: 'suppliers', name: 'Suppliers', icon: ShoppingBagIcon },
                { id: 'buyers', name: 'Buyers', icon: UserGroupIcon },
                { id: 'approvals', name: 'Approvals', icon: CheckCircleIcon },
                { id: 'issues', name: 'Issues', icon: ExclamationIcon },
                { id: 'analytics', name: 'Analytics', icon: ChartBarIcon }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Platform Status Banner */}
                <div className="bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 rounded-2xl p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold mb-2">🚀 Platform Status: Excellent</h3>
                      <p className="text-purple-100 mb-3">
                        All systems operational. User growth at {adminData.platformMetrics.userGrowth}% this month. 
                        Revenue up {adminData.platformMetrics.revenueGrowth}%.
                      </p>
                      <div className="flex items-center space-x-4 text-sm">
                        <span className="flex items-center">
                          <UsersIcon className="h-4 w-4 mr-1" />
                          {adminData.stats.totalUsers} total users
                        </span>
                        <span className="flex items-center">
                          <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                          {formatCurrency(adminData.stats.totalRevenue)} total revenue
                        </span>
                        <span className="flex items-center">
                          <StarIcon className="h-4 w-4 mr-1" />
                          {adminData.stats.platformRating} platform rating
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold">{adminData.platformMetrics.userGrowth}%</div>
                      <div className="text-purple-100 text-sm">User Growth</div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Quick Actions */}
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <SparklesIcon className="h-5 w-5 mr-2 text-purple-600" />
                      Quick Actions
                    </h3>
                    <div className="space-y-3">
                      <button className="w-full flex items-center justify-between p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center mr-3">
                            <CheckCircleIcon className="h-5 w-5 text-yellow-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">Review Pending Approvals</p>
                            <p className="text-sm text-gray-600">{adminData.stats.pendingApprovals} items</p>
                          </div>
                        </div>
                        <ArrowUpIcon className="h-5 w-5 text-gray-400" />
                      </button>
                      
                      <button className="w-full flex items-center justify-between p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                            <ExclamationIcon className="h-5 w-5 text-red-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">Handle Reported Issues</p>
                            <p className="text-sm text-gray-600">{adminData.stats.reportedIssues} issues</p>
                          </div>
                        </div>
                        <ArrowUpIcon className="h-5 w-5 text-gray-400" />
                      </button>
                      
                      <button className="w-full flex items-center justify-between p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                            <UsersIcon className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">User Management</p>
                            <p className="text-sm text-gray-600">Manage all users</p>
                          </div>
                        </div>
                        <ArrowUpIcon className="h-5 w-5 text-gray-400" />
                      </button>
                    </div>
                  </div>

                  {/* Recent Activity */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <ClockIcon className="h-5 w-5 mr-2 text-blue-600" />
                      Recent Activity
                    </h3>
                    <div className="space-y-3">
                      {adminData.recentActivity.slice(0, 4).map((activity) => (
                        <div key={activity.id} className="flex items-center space-x-3 p-3 bg-white rounded-lg shadow-sm">
                          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                            {getActivityIcon(activity.type)}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                            <p className="text-xs text-gray-600">{activity.user} • {formatTime(activity.timestamp)}</p>
                          </div>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(activity.status)}`}>
                            {activity.status.replace('_', ' ')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* User Management Tab */}
            {activeTab === 'users' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">User Management</h3>
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <SearchIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Suppliers */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <ShoppingBagIcon className="h-5 w-5 mr-2 text-blue-600" />
                      Top Suppliers
                    </h4>
                    <div className="space-y-3">
                      {adminData.topSuppliers.map((supplier) => (
                        <div key={supplier.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-sm font-bold text-blue-600">
                                {supplier.name.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{supplier.name}</p>
                              <p className="text-sm text-gray-600">{supplier.location}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(supplier.status)}`}>
                              {supplier.status}
                            </span>
                            <button className="p-1 text-gray-400 hover:text-gray-600">
                              <EyeIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Buyers */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <UserGroupIcon className="h-5 w-5 mr-2 text-green-600" />
                      Top Buyers
                    </h4>
                    <div className="space-y-3">
                      {adminData.topBuyers.map((buyer) => (
                        <div key={buyer.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                              <span className="text-sm font-bold text-green-600">
                                {buyer.name.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{buyer.name}</p>
                              <p className="text-sm text-gray-600">{buyer.location}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(buyer.status)}`}>
                              {buyer.status}
                            </span>
                            <button className="p-1 text-gray-400 hover:text-gray-600">
                              <EyeIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Approvals Tab */}
            {activeTab === 'approvals' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Pending Approvals</h3>
                  <span className="text-sm text-gray-500">{adminData.stats.pendingApprovals} items pending</span>
                </div>
                
                <div className="space-y-4">
                  {adminData.pendingApprovals.map((approval) => (
                    <div key={approval.id} className="bg-white rounded-xl border border-gray-200 p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            approval.type === 'supplier' ? 'bg-blue-100' : 'bg-green-100'
                          }`}>
                            {approval.type === 'supplier' ? (
                              <ShoppingBagIcon className="h-6 w-6 text-blue-600" />
                            ) : (
                              <UserGroupIcon className="h-6 w-6 text-green-600" />
                            )}
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">{approval.name}</h4>
                            <p className="text-sm text-gray-600">{approval.email}</p>
                            <p className="text-xs text-gray-500">Category: {approval.category}</p>
                            <p className="text-xs text-gray-500">Submitted: {approval.submittedDate}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <button className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm">
                            Approve
                          </button>
                          <button className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm">
                            Reject
                          </button>
                          <button className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm">
                            View Details
                          </button>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm text-gray-600 mb-2">Documents submitted:</p>
                        <div className="flex space-x-2">
                          {approval.documents.map((doc, index) => (
                            <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
                              {doc}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Issues Tab */}
            {activeTab === 'issues' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Reported Issues</h3>
                  <span className="text-sm text-gray-500">{adminData.stats.reportedIssues} issues reported</span>
                </div>
                
                <div className="space-y-4">
                  {adminData.reportedIssues.map((issue) => (
                    <div key={issue.id} className="bg-white rounded-xl border border-gray-200 p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            issue.severity === 'high' ? 'bg-red-100' : 'bg-orange-100'
                          }`}>
                            <ExclamationIcon className={`h-6 w-6 ${
                              issue.severity === 'high' ? 'text-red-600' : 'text-orange-600'
                            }`} />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">{issue.type.charAt(0).toUpperCase() + issue.type.slice(1)} Issue</h4>
                            <p className="text-sm text-gray-600">{issue.description}</p>
                            <p className="text-xs text-gray-500">Reported by: {issue.user || issue.users?.join(', ')}</p>
                            <p className="text-xs text-gray-500">Date: {issue.reportedDate}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            issue.severity === 'high' ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'
                          }`}>
                            {issue.severity} priority
                          </span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(issue.status)}`}>
                            {issue.status}
                          </span>
                          <button className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm">
                            Investigate
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Platform Analytics</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6">
                    <h4 className="font-semibold text-gray-900 mb-4">Growth Metrics</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">User Growth</span>
                        <span className="font-semibold text-green-600">+{adminData.platformMetrics.userGrowth}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Revenue Growth</span>
                        <span className="font-semibold text-green-600">+{adminData.platformMetrics.revenueGrowth}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Response Rate</span>
                        <span className="font-semibold">{adminData.platformMetrics.responseRate}%</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6">
                    <h4 className="font-semibold text-gray-900 mb-4">User Engagement</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Avg Session Duration</span>
                        <span className="font-semibold">{adminData.platformMetrics.avgSessionDuration}m</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Bounce Rate</span>
                        <span className="font-semibold">{adminData.platformMetrics.bounceRate}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Satisfaction Score</span>
                        <span className="font-semibold">{adminData.platformMetrics.satisfactionScore}/5</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 